/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import {
  Button,
  ConditionalWrapper,
  Grid,
  Icon,
  List,
  Spacer,
  useAsyncEffect,
} from "@cd2/components"
import { P } from "@cd2/text/build/es6"
import { format, sub } from "date-fns"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { LinkButton } from "../../../Components/Buttons/LinkButton"
import { useGlobalState } from "../../../Components/GlobalStateProvider"
import { KeyValueList } from "../../../Components/KeyValueList"
import { QuickToolTip } from "../../../Components/QuickTooltip"
import { icons } from "../../../constants/icons"
import { buildRoute, eRoutes } from "../../../routes"
import { IRecord } from "../../../types/schema"
import { fetchPricesForCustomer } from "../../../utils/fetchPricesForCustomer"
import { calculatePriceFromMargin, formatPrice } from "../../../utils/utils"
import { ProductMasterThumbnail } from "../../CustomerArea/components/ProductMasterThumbnail"
import { IBranchSettings, useSettings } from "../../../utils/useSettings"
import {
  calculateMarginFromPrice,
  formatMargin,
} from "../../../stories/quoteOrders/quoteOrderUtils"
import { convertPriceCurrency, getCurrencyOption } from "../../../utils/currency"
import { ButtonsWrapper } from "../../../Components/ButtonsWrapper"
import { CopyLink } from "../../System/FileGenerationsTable"

const productAttrs = [
  "stock_code",
  "legacy_stock_code",
  "body",
  "line_item_description",
  "cost_price_pennies",
  "price_updated_at",
  "preferred_supplier_list_price_pennies",
] as const
type IProduct = IRecord<"products", (typeof productAttrs)[number]> & {
  product_master: IProductMaster
  preferred_supplier: IRecord<"suppliers", "name" | "currency_name">
  suppliers: IRecord<"suppliers", "name" | "currency_name">[]
  branch?: IRecord<"branches", "currency_name">
  product_group?: IRecord<"product_groups", any> & { hide_supplier_info?: boolean }
}
type IProductMaster = IRecord<"product_masters", "stock_code"> & {
  products: (IRecord<"products", "stock_code" | "body"> & { branch: IRecord<"branches", "name"> })[]
  brand: IRecord<"brands", "name" | "fileboyLogoId">
  categories: IRecord<"categories", "name" | "isPrimary">[]
}

export function ProductRecommendedPriceButton({
  handleSetMargin,
  productId,
  costPricePennies,
}: {
  handleSetMargin: (margin: number) => void
  productId: string
  costPricePennies?: number | null
}) {
  const { currency } = useGlobalState()
  const { margin } = useAveragePrice({ productId })
  if (!margin) {
    return null
  }
  return (
    <ButtonsWrapper>
      <Button buttonStyle="solid" onClick={() => (margin ? handleSetMargin(margin) : {})}>
        Recommended price:{" "}
        {formatPrice(calculatePriceFromMargin(costPricePennies ?? 0, margin ?? 0), {
          currency,
          isPennies: true,
        })}
      </Button>
    </ButtonsWrapper>
  )
}

export function ProductInfoDisplayPanel({
  productId,
  customerId,
  isOpen,
  hideResourceId,
  displayCurrency,
  footer,
}: {
  productId: string
  customerId: string
  isOpen?: boolean
  hideResourceId?: string
  displayCurrency?: string
  footer?: React.ReactNode
}) {
  const { averageMargin, margin } = useAveragePrice({ productId })
  const { displayAverageMargins } = useSettings()
  const { currentUser, currency } = useGlobalState()
  const currencySymbol = getCurrencyOption(displayCurrency)?.symbol ?? currency
  const skeem = window.skeem

  const [product, setProduct] = useState<IProduct | null>(null)
  const [hasOpened, setHasOpened] = useState(false)
  // const stockData = useStockData({ productIds: [productId] })

  useEffect(() => {
    fetchProduct()
  }, [productId, customerId])

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true)
    }
  }, [isOpen])

  async function fetchProduct() {
    const product = await skeem.fetch2<IProduct>({
      first: {
        name: "products",
        attributes: [
          ...productAttrs,
          { name: "branch", attributes: ["currency_name"] },
          { name: "preferred_supplier", attributes: ["name", "currency_name"] },
          { name: "suppliers", attributes: ["name", "currency_name"] },
          { name: "product_group", attributes: ["hide_supplier_info"] },
          {
            name: "product_master",
            attributes: [
              "stock_code",
              { name: "brand", attributes: ["name", "fileboyLogoId"] },
              { name: "categories", attributes: ["name", "isPrimary"] },
            ],
          },
        ],
        filter: { matches: { id: productId } },
      },
    })
    setProduct(product)
  }

  function currencyConvertPrice(price: number) {
    return convertPriceCurrency(
      price,
      currentUser.branch.euro_pound_exchange_rate,
      product?.branch?.currency_name,
      currencySymbol,
    )
  }

  return (
    <Grid
      css={css(
        css`
          border-bottom: 3px solid #666;
          background-color: #f5f5f5;
          padding: 16px;
          user-select: text;
        `,
        !isOpen &&
          css`
            border-width: 0;
            height: 0;
            padding: 0;
            overflow: hidden;
          `,
      )}
      columns={[{ fr: 1 }, "max-content"]}
    >
      {hasOpened && (
        <PreviousPricingData
          productId={productId}
          customerId={customerId}
          hideResourceId={hideResourceId}
          displayCurrency={displayCurrency}
          footer={footer}
        />
      )}
      <Grid gap={16}>
        <Grid columns={[60, { fr: 1 }]} gap={8}>
          <div>
            <ProductMasterThumbnail productId={productId} />
          </div>
          <div>
            <div
              css={css`
                font-weight: 600;
                font-size: 15px;
                padding: 0 0 12px;
              `}
            >
              <CopyLink text={product?.stock_code ?? ""}>
                <Icon icon={icons.copy} /> {product?.stock_code ?? ""}
              </CopyLink>
            </div>
            {product?.product_master?.categories && (
              <React.Fragment>
                <List size={6} horizontal>
                  {product.product_master.categories
                    .sort((a, _b) => (a.isPrimary ? -1 : 1))
                    .map(category => (
                      <span
                        key={category.id}
                        css={css`
                          font-size: 12px;
                          color: #666;
                        `}
                      >
                        {category.name}
                      </span>
                    ))}
                </List>
              </React.Fragment>
            )}
            <div
              css={css`
                font-size: 13px;
              `}
            >
              {product?.product_master && (
                <Link
                  to={buildRoute(eRoutes["/product/:stock_code"], {
                    stock_code: encodeURIComponent(product.product_master.stock_code),
                  })}
                  target="_blank"
                  css={css`
                    color: #666;
                    border-bottom: 1px dashed #666;
                    font-size: 12px;
                    display: block;
                    width: max-content;
                  `}
                >
                  Product info <Icon icon={icons.external} />
                </Link>
              )}
            </div>
          </div>
        </Grid>
        <div>
          <KeyValueList
            labelWidth={140}
            data={[
              !product?.product_group?.hide_supplier_info
                ? {
                    label: "Preferred Supplier",
                    value: product?.preferred_supplier?.name,
                  }
                : null,
              {
                label: "Supplier List Price",
                value: product?.preferred_supplier_list_price_pennies
                  ? formatPrice(currencyConvertPrice(product.preferred_supplier_list_price_pennies)!, {
                      isPennies: true,
                      currency: currencySymbol,
                    })
                  : undefined,
              },
              { label: "Price Last updated", value: { date: product?.price_updated_at } },
              displayAverageMargins
                ? { label: "Avg % across branches (3mos)", value: averageMargin }
                : null,
            ].filter(Boolean)}
            inline
          />
          <Spacer size={16} />
        </div>
      </Grid>
    </Grid>
  )
}

type IQuoteItem = IRecord<"quote_items", "price_pennies" | "cost_price_pennies"> & {
  quote: {
    identifier: string
    created_at: string
    currency_name: string
  }
}
type IOrderItem = IRecord<"order_items", "price_pennies" | "cost_price_pennies"> & {
  order: {
    identifier: string
    created_at: string
    currency_name: string
  }
}

type IInvoiceItem = IRecord<
  "invoice_items",
  "invoice_date" | "price_pennies" | "cost_price_pennies"
> & {
  product: IRecord<"products"> & {
    suppliers: IRecord<"suppliers", "currency_name">[]
    preferred_supplier: IRecord<"suppliers", "currency_name">
  }
}

interface IPreviousPricesData {
  universal: {
    identifier: string
    pricePennies: number | null
    discount_percent_value?: number | null
    sourceType: string
    sourceName: string
    priceBandId?: string
  }[]
  lists: {
    identifier: string
    pricePennies: number | null
    discount_percent_value?: number | null
    sourceType: string
    sourceName: string
    priceBandId?: string
    currencyName: string
  }[]
  prices: {
    date: string
    pricePennies: number | null
    costPricePennies: number | null
    currencyName: string
  }[]
  quotes: {
    identifier: string
    date: string
    pricePennies: number | null
    costPricePennies: number | null
    currencyName: string
  }[]
  orders: {
    identifier: string
    date: string
    pricePennies: number | null
    costPricePennies: number | null
    currencyName: string
  }[]
}

const priceCss = css`
  font-weight: 600;
  font-size: 15px;
`

function PreviousPricingData({
  productId,
  customerId,
  hideResourceId,
  displayCurrency,
  footer,
}: {
  productId: string
  customerId: string
  hideResourceId?: string
  displayCurrency?: string
  footer?: React.ReactNode
}) {
  const { hideProductInfoStandardPricing } = useSettings()
  const skeem = window.skeem
  const { currency, currentUser } = useGlobalState()

  const currencySymbol = getCurrencyOption(displayCurrency)?.symbol ?? currency

  function currencyConvertPrice(price: number, fromCurrency: string) {
    return (
      convertPriceCurrency(
        price,
        currentUser?.branch?.euro_pound_exchange_rate,
        fromCurrency,
        displayCurrency,
      ) ?? 0
    )
  }

  const [_previousPricingDataLists, setPreviousPricingDataLists] = useState<
    IPreviousPricesData["lists"] | null
  >(null)
  const previousPricingDataLists = hideProductInfoStandardPricing
    ? _previousPricingDataLists?.filter(
        item =>
          item.sourceType !== "standard" &&
          item.sourceName.toLowerCase() !== "standard" &&
          item.identifier.toLowerCase() !== "standard",
      )
    : _previousPricingDataLists

  const [previousPricingDataPrices, setPreviousPricingDataPrices] = useState<
    IPreviousPricesData["prices"] | null
  >(null)
  const [previousPricingDataQuotes, setPreviousPricingDataQuotes] = useState<
    IPreviousPricesData["quotes"] | null
  >(null)
  const [previousPricingDataOrders, setPreviousPricingDataOrders] = useState<
    IPreviousPricesData["orders"] | null
  >(null)

  useEffect(() => {
    fetchPreviousQuotePriceData(productId, customerId)
    fetchPreviousOrderPriceData(productId, customerId)
    fetchPriceBandData(productId, customerId)
    fetchPreviousInvoicePrices(productId, customerId)
  }, [productId, customerId])

  async function fetchPreviousQuotePriceData(productId: string, customerId: string) {
    const quoteItems = await skeem.fetch<IQuoteItem>({
      quote_items: {
        attributes: [
          "price_pennies",
          "cost_price_pennies",
          {
            name: "quote",
            attributes: ["identifier", "created_at", "currency_name"],
          },
        ],
        filter: {
          and: [
            hideResourceId && { not: { eq: [{ path: ["quote"] }, { value: hideResourceId }] } },
            { eq: [{ path: ["product"] }, { value: productId }] },
            { eq: [{ path: ["quote", "customer", "id"] }, { value: customerId }] },
          ].filter(Boolean),
        },
        sort: {
          by: { path: ["quote", "created_at"] },
          direction: "DESC",
        },
        paginate: { page: 1, size: 3 },
      },
    })
    const priceData = quoteItems
      .filter(item => !!item.quote)
      .filter(item => item.price_pennies)
      .map(item => ({
        date: item.quote.created_at,
        pricePennies: item.price_pennies!,
        identifier: item.quote.identifier,
        costPricePennies: item.cost_price_pennies,
        currencyName: item.quote.currency_name,
      }))
    setPreviousPricingDataQuotes(priceData)
    return priceData
  }

  async function fetchPreviousOrderPriceData(productId: string, customerId: string) {
    const orderItems = await skeem.fetch<IOrderItem>({
      order_items: {
        attributes: [
          "price_pennies",
          "cost_price_pennies",
          {
            name: "order",
            attributes: ["identifier", "created_at", "currency_name"],
          },
        ],
        filter: {
          and: [
            hideResourceId && { not: { eq: [{ path: ["order"] }, { value: hideResourceId }] } },
            { eq: [{ path: ["product"] }, { value: productId }] },
            { eq: [{ path: ["order", "customer", "id"] }, { value: customerId }] },
          ].filter(Boolean),
        },
        sort: {
          by: { path: ["order", "created_at"] },
          direction: "DESC",
        },
        paginate: { page: 1, size: 3 },
      },
    })
    const priceData = orderItems
      .filter(item => !!item.order)
      .filter(item => item.price_pennies)
      .map(item => ({
        date: item.order.created_at,
        pricePennies: item.price_pennies!,
        identifier: item.order.identifier,
        costPricePennies: item.cost_price_pennies,
        currencyName: item.order.currency_name,
      }))
    setPreviousPricingDataOrders(priceData)
    return priceData
  }

  async function fetchPriceBandData(productId: string, customerId: string) {
    const pricesForCustomer = await fetchPricesForCustomer(skeem, productId, customerId)
    const priceData = pricesForCustomer.map(list => ({
      identifier: list.name,
      pricePennies: list.price_pennies,
      discount_percent_value: list.discount_percent_value,
      sourceType: list.sourceType,
      sourceName: list.sourceName,
      priceBandId: list.priceBandId,
      currencyName: list.currency_name ?? "",
    }))

    setPreviousPricingDataLists(priceData)
    return priceData
  }

  async function fetchPreviousInvoicePrices(productId: string, customerId: string) {
    const invoiceItems = await skeem.fetch<IInvoiceItem>({
      invoice_items: {
        attributes: [
          "invoice_date",
          "price_pennies",
          "cost_price_pennies",
          {
            name: "product",
            attributes: [
              { name: "suppliers", attributes: ["currency_name"] },
              { name: "preferred_supplier", attributes: ["currency_name"] },
            ],
          },
        ],
        filter: {
          matches: {
            customer: customerId,
            product: productId,
            type: "Invoice",
          },
        },
        sort: {
          by: "invoice_date",
          direction: "DESC",
        },
        paginate: { page: 1, size: 3 },
      },
    })

    const priceData = invoiceItems
      .filter(item => item.price_pennies)
      .map(item => ({
        date: item.invoice_date,
        pricePennies: item.price_pennies!,
        costPricePennies: item.cost_price_pennies,
        currencyName: "",
      }))
    setPreviousPricingDataPrices(priceData)
    return priceData
  }

  const titleCss = css`
    font-weight: 600;
    color: #666;
    font-size: 12px;
  `

  function SourceMessage({
    sourceType,
    sourceName,
    priceBandId,
  }: {
    sourceType: string
    sourceName: string
    priceBandId?: string
  }) {
    const linkProps = {
      rightIcon: icons.external,
      extendTheme: { padding: 8 },
      buttonStyle: "text" as const,
      color: "white",
      buttonColor: "#efefef",
      external: true,
    }

    if (sourceType === "product_group_discount") {
      return (
        <Grid columns={["auto", "max-content"]} gap={8}>
          <P color="white">This price is from product group discount ({sourceName})</P>
          <LinkButton
            to={buildRoute(eRoutes["/customers/:id/product-group-discounts"], { id: customerId })}
            {...linkProps}
          >
            View
          </LinkButton>
        </Grid>
      )
    } else if (sourceType === "eos_price_band") {
      return (
        <React.Fragment>
          <Grid columns={["auto", "max-content"]} gap={8}>
            <P color="white">This price is from Fortus Live price band ({sourceName})</P>
            <LinkButton
              to={buildRoute(
                eRoutes["/customers/:id/price-bands"],
                { id: customerId },
                { qs: { priceBandId: priceBandId ?? "" } },
              )}
              {...linkProps}
            >
              View
            </LinkButton>
          </Grid>
        </React.Fragment>
      )
    } else if (sourceType === "sage_price_band") {
      return (
        <Grid columns={["auto", "max-content"]} gap={8}>
          <P color="white">This price is from Sage price band ({sourceName}) </P>
          <LinkButton
            to={buildRoute(
              eRoutes["/customers/:id/price-bands"],
              { id: customerId },
              { qs: { priceBandId: priceBandId ?? "" } },
            )}
            {...linkProps}
          >
            View
          </LinkButton>
        </Grid>
      )
    } else if (sourceType === "universal_price_band") {
      return (
        <Grid columns={["auto", "max-content"]} gap={8}>
          <P color="white">This price is from a universal price band ({sourceName}) </P>
          <LinkButton
            to={buildRoute(eRoutes["/stock/universal-price-bands/:id"], { id: priceBandId })}
            {...linkProps}
          >
            View
          </LinkButton>
        </Grid>
      )
    } else if (sourceType === "shared_price_band") {
      return (
        <Grid columns={["auto", "max-content"]} gap={8}>
          <P color="white">This price is from a shared price band ({sourceName}) </P>
          <LinkButton
            to={buildRoute(eRoutes["/stock/shared-price-bands/:id"], { id: priceBandId })}
            {...linkProps}
          >
            View
          </LinkButton>
        </Grid>
      )
    } else {
      console.warn(`Unknown sourceType: ${sourceType} for sourceName: ${sourceName}`)
    }
    return null
  }

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <Grid columns={{ repeat: { times: "auto-fit", size: { min: 200, max: { fr: 1 } } } }}>
        <Grid
          gap={12}
          columns={{ repeat: { times: "auto-fit", size: { min: 200, max: { fr: 1 } } } }}
          alignItems="start"
        >
          <Grid gap={12}>
            <b css={titleCss}>Price Bands</b>
            {previousPricingDataLists && previousPricingDataLists.length === 0 && <i>No results</i>}
            {previousPricingDataLists?.map(list => (
              <div key={list.identifier}>
                <div css={priceCss}>
                  <ConditionalWrapper
                    Wrapper={({ children }) => (
                      <QuickToolTip content={<SourceMessage {...list} />} interactive>
                        {children}
                      </QuickToolTip>
                    )}
                    condition={list.sourceType !== "standard"}
                  >
                    {formatPrice(currencyConvertPrice(list.pricePennies!, list.currencyName), {
                      isPennies: true,
                      currency: currencySymbol,
                    })}
                  </ConditionalWrapper>
                </div>
                {list.discount_percent_value ? (
                  <div
                    css={css`
                      border-bottom: 1px dashed #999;
                      display: block;
                      font-size: 12px;
                      width: max-content;
                    `}
                  >
                    {list.discount_percent_value}%
                  </div>
                ) : undefined}
                <div
                  css={css`
                    font-size: 12px;
                  `}
                >
                  {list.identifier}
                </div>
              </div>
            ))}
          </Grid>
          <Grid gap={12}>
            <b css={titleCss}>Previous Invoices</b>
            {previousPricingDataPrices && previousPricingDataPrices.length === 0 && (
              <i>No results</i>
            )}
            {previousPricingDataPrices
              ?.sort((a, b) => (new Date(a.date) < new Date(b.date) ? 1 : -1))
              .map((invoice, i) => (
                <div key={i}>
                  <CostWithMargin
                    costPricePennies={currencyConvertPrice(
                      invoice.costPricePennies!,
                      invoice.currencyName,
                    )}
                    pricePennies={currencyConvertPrice(invoice.pricePennies!, invoice.currencyName)}
                    currency={currencySymbol}
                  />
                  <div
                    css={css`
                      font-size: 12px;
                    `}
                  >
                    {invoice.date && format(new Date(invoice.date), "dd/MM/yyyy")}
                  </div>
                </div>
              ))}
          </Grid>
          <Grid gap={12}>
            <b css={titleCss}>Previous Orders</b>
            {previousPricingDataOrders && previousPricingDataOrders.length === 0 && (
              <i>No results</i>
            )}
            {previousPricingDataOrders?.map(order => (
              <div key={order.identifier}>
                <CostWithMargin
                  costPricePennies={currencyConvertPrice(
                    order.costPricePennies!,
                    order.currencyName,
                  )}
                  pricePennies={currencyConvertPrice(order.pricePennies!, order.currencyName)}
                  currency={currencySymbol}
                />

                <div
                  css={css`
                    font-size: 12px;
                  `}
                >
                  {order.identifier} - {order.date && format(new Date(order.date), "dd/MM/yyyy")}
                </div>
              </div>
            ))}
          </Grid>
          <Grid gap={12}>
            <b css={titleCss}>Previous Quotes</b>
            {previousPricingDataQuotes && previousPricingDataQuotes.length === 0 && (
              <i>No results</i>
            )}
            {previousPricingDataQuotes?.map(quote => (
              <div key={quote.identifier}>
                <CostWithMargin
                  costPricePennies={currencyConvertPrice(
                    quote.costPricePennies!,
                    quote.currencyName,
                  )}
                  pricePennies={currencyConvertPrice(quote.pricePennies!, quote.currencyName)}
                  currency={currencySymbol}
                />
                <div
                  css={css`
                    font-size: 12px;
                  `}
                >
                  {quote.identifier} - {quote.date && format(new Date(quote.date), "dd/MM/yyyy")}
                </div>
              </div>
            ))}
          </Grid>
        </Grid>
      </Grid>
      {footer}
    </div>
  )
}

function CostWithMargin({
  costPricePennies,
  pricePennies,
  currency,
}: {
  costPricePennies: number | null
  pricePennies: number
  currency: "" | "£" | "€"
}) {
  return (
    <List horizontal>
      <div css={priceCss}>{formatPrice(pricePennies, { isPennies: true, currency })}</div>
      {costPricePennies && (
        <QuickToolTip
          content={`Margin based on a cost price of ${formatPrice(costPricePennies, {
            currency,
            isPennies: true,
          })} on this date`}
        >
          <div
            css={css`
              border-bottom: 1px dashed #999;
              display: block;
              font-size: 12px;
            `}
          >
            {((((pricePennies ?? 0) - costPricePennies) / (pricePennies ?? 0)) * 100).toFixed(2)}%
          </div>
        </QuickToolTip>
      )}
    </List>
  )
}

function useAveragePrice({ productId }: { productId?: string }) {
  // - Get the last 3 months of order items for the product
  //   - Get the average order item price of all sales of this product
  //   - Get the average cost price of all sales of this product
  //   - Calculate the margin
  const skeem = window.skeem

  const [averageMargin, setAverageMargin] = useState<null | number>(null)

  async function loadOrderItems() {
    const branches = await skeem.fetch2<(IRecord<"branches"> & { settings: IBranchSettings })[]>({
      model: {
        name: "branches",
        attributes: ["settings"],
        filter: {
          and: [
            { not: { matches: { name: { like: "test" } } } },
            { not: { matches: { name: { like: "demo" } } } },
            { not: { matches: { name: { like: "training" } } } },
          ],
        },
      },
    })
    const contributingBranches = branches.filter(b => b.settings?.contributeToAverageMargins)
    const product = await skeem.fetch2<
      IRecord<"products"> & {
        order_items: IRecord<"order_items", "price_pennies" | "cost_price_pennies">[]
      }
    >({
      first: {
        name: "products",
        attributes: [
          {
            as: "order_items",
            value: {
              path: [
                "product_master",
                {
                  name: "products",
                  filter: { matches: { branch: { or: contributingBranches.map(v => v.id) } } },
                },
                {
                  name: "order_items",
                  attributes: ["cost_price_pennies", "price_pennies"],
                  filter: { matches: { created_at: { gte: sub(new Date(), { months: 3 }) } } },
                },
              ],
            },
          },
        ],
        filter: { matches: { id: productId } },
      },
    })
    const items = product.order_items
    const validItems: typeof items = items.filter(
      i =>
        i.cost_price_pennies &&
        i.price_pennies &&
        calculateMarginFromPrice(i.cost_price_pennies ?? 0, i.price_pennies ?? 0) >= 15,
    )
    if (validItems.length === 0) {
      setAverageMargin(null)
      return
    }

    const averageCostPrice =
      validItems.reduce((total, item) => total + item.cost_price_pennies!, 0) / validItems.length
    const averageSalesPrice =
      validItems.reduce((total, item) => total + item.price_pennies!, 0) / validItems.length
    const margin = calculateMarginFromPrice(averageCostPrice, averageSalesPrice)
    if (!isFinite(margin)) {
      setAverageMargin(null)
      return
    }
    setAverageMargin(margin)
  }

  useAsyncEffect(async () => {
    await loadOrderItems()
  }, [productId])

  return {
    averageMargin: averageMargin ? formatMargin(averageMargin) : null,
    margin: averageMargin,
  }
}
